import axios from 'axios';
import FormData from 'form-data';

import auth from '../libs/auth';
import { AxiosCacheRequestConfig } from '../libs/AxiosCache';
import Spec from './Spec';
import GenericResponse from './GenericResponse';
import APITools from './APITools';

interface StudentInfo {
  studentID: string;
  firstName: string;
  lastName: string;
  grade: string;
  letterGrade: string;
  submittedAssignmentData: string;
}

class Assignment {
  id: string;
  name: string;
  type: string;
  points: string;
  students: StudentInfo[];
  grade?: string;
  letterGrade?: string;
  checked?: boolean;

  constructor(assignment: Assignment) {
    this.id = assignment.id;
    this.name = assignment.name;
    this.type = assignment.type;
    this.points = assignment.points;
    this.students = assignment.students;
  }

  public static async getAll(courseID: String): Promise<Assignment[]> {
    const data = (
      await axios.post('/populi/get-course-instance-assignments', {
        instanceID: courseID,
      })
    ).data;

    return (data.assignments || []).map((item: any) => ({
      id: item.assignmentID as string,
      name: item.name as string,
      type: item.type as string,
      points: item.points as string,
      students: (item.studentInfo || []) as StudentInfo[],
      grade: item.grade as string,
      letterGrade: item.letterGrade as string,
      checked: true,
    }));
  }

  public static async getSpecList(assignmentID: string): Promise<Spec[]> {
    const data = (await axios.get(`/specs?assignmentID=${assignmentID}`)).data;

    return data;
  }

  public static async cloneSpecs(
    sourceAssignmentID: string,
    targetAssignmentID: string
  ) {
    const data = (
      await axios.post('/admin/assignment/clone', {
        sourceAssignmentID,
        targetAssignmentID,
      })
    ).data;

    return data;
  }

  public static async addSpec(assignmentID: string, specID: string) {
    const data = (
      await axios.post('/admin/assignment/spec', {
        assignmentID,
        specID,
      })
    ).data;
    return data;
  }

  public static async removeSpec(assignmentID: string, specID: string) {
    const data = (
      await axios.delete('/admin/assignment/spec', {
        data: { assignmentID, specID },
      })
    ).data;
    return data;
  }

  public static async setSpecs(assignmentID: string, specIDs: string[]) {
    const { data } = await axios.put('/admin/assignment/specs', {
      assignmentId: assignmentID,
      specIds: specIDs
    });
    return data;
  }

  public async addComment({
    studentID,
    specID,
    comment,
    file,
    internal,
  }: {
    studentID: string;
    specID?: string;
    comment?: string;
    file?: File;
    internal?: boolean;
  }): Promise<GenericResponse> {
    const assignmentID = this.id;
    const form = new FormData();

    form.append('file', file);
    form.append('personID', studentID);
    form.append('assignmentID', assignmentID);
    form.append('comment', comment);
    form.append('internal', internal);

    if (!!specID) {
      form.append('specID', specID);
    }

    const { data } = await axios.post('/populi/assignment/comment', form, {
      headers: 'multipart/form-data',
    });

    return data;
  }

  public async getComments(params: { personID: string }): Promise<any> {
    const queryString = APITools.parseQueryParams({
      ...params,
      assignmentID: this.id,
    });
    const data = await axios.get(`/populi/assignment/comments${queryString}`);

    return data;
  }

  static async saveMoodleAssignmentGrade(query: { [key: string]: any } = {}, config?: AxiosCacheRequestConfig): Promise<any> {
    const session = auth.getSession();
    const data = await axios.post('/student/course/assignments/save-grade', {...query, token: session?.accessToken});

    return data;
  }

    static async saveMoodleAssignment(query: { [key: string]: any } = {}, config?: AxiosCacheRequestConfig): Promise<any> {
    const session = auth.getSession();

    return await axios.post('/student/course/save-assignment', {...query, token: session?.accessToken});
  }
}

export default Assignment;
